<template>
  <section class="admin-dashboard" data-testid="admin-system-info">
    <el-card shadow="never" class="admin-dashboard__card">
      <el-row :gutter="20">
        <el-col :span="12" :xs="24">
          <table>
            <tbody>
              <tr v-for="item in leftRows" :key="item.label">
                <th>{{ item.label }}</th>
                <td>{{ displayValue(item) }}</td>
              </tr>
            </tbody>
          </table>
        </el-col>
        <el-col :span="12" :xs="24">
          <table>
            <tbody>
              <tr v-for="item in rightRows" :key="item.label">
                <th>{{ item.label }}</th>
                <td>{{ displayValue(item) }}</td>
              </tr>
            </tbody>
          </table>
        </el-col>
      </el-row>
    </el-card>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'

import { getAdminSystemInfo, type AdminSystemInfo } from '@/api/admin/system'
import packageInfo from '../../../../package.json'

interface DashboardRow {
  label: string
  key?: keyof AdminSystemInfo
  text?: string
  suffix?: string
}

const info = ref<AdminSystemInfo>({})
const { t } = useI18n()
const leftRows = computed<DashboardRow[]>(() => [
  { label: t('admin.dashboard.systemName'), text: t('admin.brand') },
  { label: t('admin.dashboard.version'), text: `v${packageInfo.version}` },
  { label: t('admin.dashboard.osName'), key: 'osName' },
  { label: t('admin.dashboard.osVersion'), key: 'osVersion' },
  { label: t('admin.dashboard.osArch'), key: 'osArch' },
  { label: t('admin.dashboard.processors'), key: 'processors' },
  { label: t('admin.dashboard.totalPhysical'), key: 'totalPhysical', suffix: 'MB' },
  { label: t('admin.dashboard.freePhysical'), key: 'freePhysical', suffix: 'MB' },
  { label: t('admin.dashboard.memoryRate'), key: 'memoryRate', suffix: '%' },
  { label: t('admin.dashboard.userLanguage'), key: 'userLanguage' }
])
const rightRows = computed<DashboardRow[]>(() => [
  { label: t('admin.dashboard.jvmName'), key: 'jvmName' },
  { label: t('admin.dashboard.javaVersion'), key: 'javaVersion' },
  { label: t('admin.dashboard.javaHome'), key: 'javaHome' },
  { label: t('admin.dashboard.userDir'), key: 'userDir' },
  { label: t('admin.dashboard.javaTotalMemory'), key: 'javaTotalMemory', suffix: 'MB' },
  { label: t('admin.dashboard.javaFreeMemory'), key: 'javaFreeMemory', suffix: 'MB' },
  { label: t('admin.dashboard.javaMaxMemory'), key: 'javaMaxMemory', suffix: 'MB' },
  { label: t('admin.dashboard.userName'), key: 'userName' },
  { label: t('admin.dashboard.systemCpuLoad'), key: 'systemCpuLoad', suffix: '%' },
  { label: t('admin.dashboard.userTimezone'), key: 'userTimezone' }
])

const displayValue = (item: DashboardRow): string => {
  if (item.text) return item.text
  const current = item.key ? info.value[item.key] : undefined
  return current === null || current === undefined || current === '' ? '' : `${current}${item.suffix || ''}`
}

onMounted(async () => {
  try {
    info.value = await getAdminSystemInfo()
  } catch {
    ElMessage.error(t('admin.dashboard.loadFailed'))
  }
})
</script>

<style scoped>
.admin-dashboard {
  min-height: calc(100vh - var(--admin-header-height) - var(--admin-tabs-height));
  padding: 15px;
}

.admin-dashboard__card {
  min-height: calc(100vh - var(--admin-header-height) - var(--admin-tabs-height) - 30px);
  border-width: 0;
}

.admin-dashboard table {
  width: 100%;
  border: 1px solid #ebeef5;
  border-collapse: collapse;
  font-size: 14px;
  line-height: 1.5;
}

.admin-dashboard th,
.admin-dashboard td {
  padding: 12px 10px;
  border: 1px solid #ebeef5;
}

.admin-dashboard th {
  width: 30%;
}

.admin-dashboard td {
  word-break: break-all;
}
</style>
